import React, { useEffect, useMemo, useState } from "react"
import dividerPrimaryGradientUrl from "../../assets/img/border/divider-primary-gradient-h.svg"
import { Box, Grid, Tooltip, Typography } from "@mui/material"
import Button from "@mui/material/Button"

import { NftBox } from "./NftBox"
import { BASE_URL } from "../../constants/constants"
import { handleBoxPurchase } from "../../api/web3/handleBoxPurchase"
import { connectHandler } from "../../utils/connectHandler"
import { useAppDispatch } from "../../store"
import LoadingButton from "@mui/lab/LoadingButton"
import { useDialog } from "../../context/DialogContext"
import { ErrorDialog } from "../dialog/ErrorDialog"
import { SuccessDialog } from "../dialog/SuccessDialog"

export const BoxList = ({ boxes, user, buyButtonText = "Buy NFT Boxes" }) => {
  const dispatch = useAppDispatch()
  const [quantities, setQuantities] = useState({})
  const [loading, setLoading] = useState(false)
  const [openErrorDialog, closeErrorDialog] = useDialog()
  const [openSuccessDialog, closeSuccessDialog] = useDialog()

  useEffect(() => {
    setQuantities((prev) => {
      const next = { ...prev }
      ;(boxes || []).forEach((box) => {
        const key = box.id
        if (next[key] === undefined) next[key] = 1
      })
      return next
    })
  }, [boxes])

  const handleQuantityChange = (key) => (value) => {
    setQuantities((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const totalBoxes = useMemo(
    () => Object.values(quantities).reduce((a, b) => a + (Number(b) || 0), 0),
    [quantities],
  )

  const totalPrice = useMemo(() => {
    return (boxes || []).reduce((sum, box) => {
      const key = box.id
      const qty = Number(quantities[key] || 0)
      const price = Number(box.price)
      return sum + qty * price
    }, 0)
  }, [boxes, quantities])

  const onPurchaseButtonClick = async () => {
    setLoading(true)
    try {
      const message = await handleBoxPurchase(
        user,
        totalBoxes,
        quantities,
        totalPrice,
      )
      openSuccessDialog({
        children: (
          <SuccessDialog
            message={message}
            closeDialog={closeSuccessDialog}
          />
        ),
      })
    } catch (e) {
      openErrorDialog({
        children: (
          <ErrorDialog
            message={e?.message || String(e)}
            closeDialog={closeErrorDialog}
          />
        ),
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box>
      <Grid
        container
        spacing={2}
        sx={{
          "& > div:nth-child(even)": {
            md: {
              borderRight: "1px solid transparent",
              borderLeft: "1px solid transparent",
              borderImage: `url(${dividerPrimaryGradientUrl}) 1 stretch`,
            },
          },
        }}
        justifyContent="center"
      >
        {boxes.map((box) => {
          const key = box.id
          const quantity = quantities[key] ?? 1

          return (
            <Grid key={key} item xs={6} sm={6} md={4} lg>
              <NftBox
                title={box.name}
                img={BASE_URL + box.image}
                alt=""
                price={box.price}
                amount={box.amount}
                quantity={quantity}
                onQuantityChange={handleQuantityChange(key)}
              />
            </Grid>
          )
        })}
      </Grid>

      <Box mt={4} mb={2} textAlign="right">
        <Typography fontSize={{ xs: "1.2rem", md: "1.7rem" }}>
          Total Boxes: <span className="primary">{totalBoxes}</span>
        </Typography>
        <Typography fontSize={{ xs: "1.2rem", md: "1.7rem" }}>
          Total Price:{" "}
          <span className="primary">{totalPrice.toFixed(6)} USDT</span>
        </Typography>
      </Box>

      <Box textAlign="right">
        <Tooltip title={!user?.address ? "Connect Wallet" : ""}>
          <span>
            {!user.address ? (
              <Button
                variant="contained"
                size="large"
                disabled={totalBoxes < 1}
                onClick={() => connectHandler(dispatch)}
              >
                Connect wallet to purchase
              </Button>
            ) : (
              <LoadingButton
                variant="contained"
                size="large"
                onClick={onPurchaseButtonClick}
                loading={loading}
                disabled={loading || totalBoxes < 1}
              >
                {buyButtonText}
              </LoadingButton>
            )}
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}
